import type { CSSProperties } from 'react'
import { escapeHtml, fromNow, stamp } from '../inventoree/format'
import { durationString, hoursString, longDateTime, trunc } from './format'
import './xdrList.css'
import { appLabelHtml, type TicketAppLabel } from '../applications/tickets/appLabel'

/**
 * Coloanele listelor de tichete - `TicketsModel` din
 * `public/js/easeedesk/easeedesk.js` si `ClosedTicketsModel` din
 * `public/js/easeedesk/closed_tickets.js` - cu ACELASI HTML pe celula,
 * aceleasi titluri, aceeasi ordine si aceeasi sortabilitate.
 *
 * O SINGURA SCHIMBARE, PESTE TOT: textul scris de om (subiectul tichetului,
 * numele oamenilor, numele firmei) trece prin `escapeHtml`. In portal se lipea
 * brut in HTML - un subiect cu `<` in el rupea celula, si nu numai. Aceeasi
 * hotarare ca la listele Inventoree deja mutate.
 */

export interface TicketTypeCell {
  type_name?: string
  short_name?: string
  color_identifier?: string
}

export interface TicketStatusCell {
  status_name?: string
  status_color?: string
  status_type?: number
}

export interface TicketPriorityCell {
  priority_name?: string
  priority_color?: string
  urgency_flag?: number
}

/**
 * Raspunsul lui `TicketSla::status()` din api, camp cu camp.
 *
 * NU are index de tip `[key: string]: unknown`. L-a avut, si el e cel care a
 * lasat coloana "SLA Details" sa citeasca ani de zile `slaStatus.sla` si
 * `slaStatus.time` - doua campuri care nu exista - fara ca vreun compilator
 * sa spuna ceva. Scrisa inchisa, o citire pe un nume gresit nu mai trece.
 */
export interface TicketSlaStatus {
  tookOverSla: number
  tookOverTime: number
  tookOverColor: string
  identifySla: number
  identifyTime: number
  identifyColor: string
  interventionSla: number
  interventionTime: number
  interventionColor: string
  /** Cronometrul aflat in curs, ales de `sla_category`. */
  currentSla: number
  currentTime: number
  currentColor: string
  timeMsg: number | string
  statusMsg: string
}

export interface TicketActionFlags {
  comment?: number
  take_over?: number
  delete?: number
  sms?: number
  assignee?: number
  user?: number
}

export interface TicketRow {
  ID: number
  DT_RowId: string
  subject: string | null
  company_id: number
  company_name: string | null
  company_color: string | null
  office_id: number | null
  office_name: string | null
  office_color?: string | null
  affected_category: number | null
  affected_user: number | null
  affected_ticket: string | number | null
  /** Numele masinilor legate, pe tichetele de categoria 2 (prima intai). */
  affected_devices?: string[]
  creator: number | null
  creator_type: number | null
  ticket_creator: string | null
  assignee: number | null
  assignee_firstname: string | null
  assignee_lastname: string | null
  escalation_responsible: string | null
  assigned_group: number | null
  group_name: string | null
  group_color: string | null
  attachments: number | null
  chat_messages: number | null
  conversation_messages?: number | null
  comments_messages?: number | null
  escalation_type: number | null
  escalation_color: string | null
  planned: number | null
  planning_color: string | null
  created: string | null
  updated: string | null
  resolved: string | null
  closed?: string | null
  sla_time: number | null
  ticket_active_time: number
  ticket_source: number | null
  ticketSourceIcon: string
  ticketSourceColor: string
  ticketSourceName: string
  /** Aplicatia de business a aparatelor tichetului: "Charisma ERP (Critical)" + cate mai sunt. */
  app_label?: TicketAppLabel | null
  /** Tichetul unui incident XDR (docs XDR 05): chenar rosu si insigna in lista. */
  xdr?: boolean
  vip: number
  actions: TicketActionFlags
  tType: TicketTypeCell | null
  tStatus: TicketStatusCell | null
  tPriority: TicketPriorityCell | null
  tCategory: { category_name?: string } | null
  tSubcategory: { subcategory_name?: string } | null
  slaStatus: TicketSlaStatus | null
  /**
   * Masura SLA dupa profil (calendar de lucru), din `SlaClockView` in api.
   * Null / lipsa = tichetul n-are profil, si coloana SLA arata ce arata azi.
   */
  slaClock?: TicketSlaClock | null
  /** Bifa de selectie a randului (pusa de pagina, nu de server). */
  __checked?: boolean
  [key: string]: unknown
}

/**
 * Termenul si timpul SLA dupa profilul tichetului (`App\Easeedesk\Sla\SlaClockView`).
 * `mode` e `business` cand ceasul merge numai in programul de lucru, `clock`
 * cand profilul e 24/7. Toate duratele sunt in secunde.
 */
export interface TicketSlaClock {
  mode: 'business' | 'clock'
  profile: string
  via: string
  elapsed: number
  sla: number
  remaining: number
  breached: boolean
  running: boolean
  /** ISO 8601 cu fusul; null cand ceasul e oprit sau termenul a trecut. */
  due_at: string | null
  /** Termenul in ora calendarului, "YYYY-MM-DD HH:MM". */
  due_local: string | null
  timezone: string | null
}

/**
 * Celula SLA dupa profil: termenul, timpul scurs colorat (aceleasi praguri ca
 * in lista de azi), si dedesubt termenul calculat dupa calendar sau "paused".
 */
export function slaClockHtml(clock: TicketSlaClock, closed = false): string {
  const sla = clock.sla
  if (!sla || sla <= 0) return ''

  const spent = clock.elapsed
  const head = `<i class="far fa-handshake" aria-hidden="true"></i> ${durationString(sla)}`
  let color = 'green'
  if (spent > sla) color = 'red'
  else if (!closed && spent >= (sla * 75) / 100) color = 'orange'

  const title = escapeHtml(`${clock.mode === 'business' ? 'Business hours' : '24/7'}${clock.profile ? ` - ${clock.profile}` : ''}`)
  let tail = ''
  if (!closed) {
    if (clock.breached) tail = `<div class="od-sla-due" style="color:red">over by ${hoursString(-clock.remaining)}</div>`
    else if (!clock.running) tail = '<div class="od-sla-due text-muted">paused</div>'
    else if (clock.due_local) tail = `<div class="od-sla-due" title="${escapeHtml(clock.timezone ?? '')}">due ${escapeHtml(clock.due_local)}</div>`
  }
  const badge = clock.mode === 'business' ? ' <i class="fas fa-business-time" aria-hidden="true"></i>' : ''

  return `<span title="${title}">${head}${badge}</span><div style="color:${color}"><i class="far fa-clock" aria-hidden="true"></i> ${hoursString(spent)}</div>${tail}`
}

/** Coloana unei liste de tichete - aceeasi forma ca `Column` din Inventoree. */
export interface TicketColumn {
  index: number
  title: string
  titleHtml?: string
  name?: string
  orderable: boolean
  visible: boolean
  width?: number
  html(row: TicketRow): string
  /** `createdCell` din DataTables: stilul pus pe CELULA, nu in ea. */
  cellStyle?(row: TicketRow): CSSProperties | undefined
  /** Clasa pusa pe rand (`$(cell).parent().addClass('vip')`). */
  rowClass?(row: TicketRow): string | undefined
}

const TICKET_URL = '/easeedesk/ticket/'

function ticketHref(id: number): string {
  return `${TICKET_URL}${Number(id)}`
}

/** Coloana 0: butonul care desfasoara randul. */
function expandHtml(row: TicketRow): string {
  return `<center class="center show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}" data-type=""><i class="fas fa-plus-circle"></i></center>`
}

/** Insignele din fata subiectului: chat, conversatie, comentarii. */
function subjectBadges(row: TicketRow): string {
  let badge = ''
  if (Number(row.chat_messages) > 0) {
    badge += '<i class="fa fa-comment color-red font-14" data-toggle="tooltip" data-placement="top" title="new chat messages" aria-hidden="true"></i> '
  }
  if (Number(row.conversation_messages) > 0) {
    badge += `<i class="fa fa-envelope font-14 color-warning" data-toggle="tooltip" data-placement="top" title="${Number(row.conversation_messages)} new conversation messages" aria-hidden="true"></i> `
  }
  if (Number(row.comments_messages) > 0) {
    badge += `<i class="far fa-comments font-14 color-red" data-toggle="tooltip" data-placement="top" title="${Number(row.comments_messages)} new comments" aria-hidden="true"></i> `
  }
  return badge
}

function subjectHtml(row: TicketRow): string {
  const badge = appLabelHtml(row.app_label) + subjectBadges(row)
  const subject = escapeHtml(row.subject)

  if (Number(row.attachments) > 0) {
    return `${badge}<i class="fa fa-paperclip" aria-hidden="true" data-toggle="tooltip" data-placement="top" title="${Number(row.attachments)} Attachments"></i> ${subject}`
  }

  return `${badge}<a href="${ticketHref(row.ID)}" target="_blank" style="color:#57636f">${subject}</a>`
}

/**
 * Celula omului / echipamentului afectat.
 *
 * Categoria 2 e un echipament: numele masinii (si "+N" cand sunt mai multe),
 * iar "device" numai cand serverul nu stie niciun nume; 1 e un contact al
 * firmei, 3 unul necunoscut. Fara nume, celula ramane goala in loc sa doboare
 * lista - comentariul e din portal, unde `affected_ticket.trunc` pe `null`
 * arunca.
 */
function affectedHtml(row: TicketRow): string {
  const category = Number(row.affected_category)
  if (!category) return ''

  if (category === 2) {
    const names = (row.affected_devices ?? []).filter((n) => n !== '')
    if (names.length === 0) {
      return '<a href="javascript:void(0)" class="show-affected-details"><i class="fas fa-desktop color-warning" aria-hidden="true"></i> device</a>'
    }
    const first = escapeHtml(names[0])
    const more = names.length > 1 ? ` +${names.length - 1}` : ''
    const title = escapeHtml(names.join(', '))
    return `<a href="javascript:void(0)" class="show-affected-details" title="${title}"><i class="fas fa-desktop color-warning" aria-hidden="true"></i> ${trunc(first, 30)}${more}</a>`
  }

  let quickInfoClass = ''
  if (category === 1) quickInfoClass = 'quick-sidebar-owner'
  if (category === 3) quickInfoClass = 'quick-sidebar-unknown'

  if (!row.affected_ticket) return ''

  const name = escapeHtml(row.affected_ticket)

  return `<a href="javascript:void(0);" title="${name}" data-id="${Number(row.affected_user)}" class="${quickInfoClass}">`
    + `<i class="fas fa-exclamation-triangle color-warning" aria-hidden="true"></i> <i class="fas fa-search" aria-hidden="true"></i> ${trunc(name, 30)}</a>`
}

/** Numele operatorului, plus cine a luat in primire la escaladarea interna. */
export function assigneeName(row: TicketRow): string {
  const full = `${escapeHtml(row.assignee_firstname)} ${escapeHtml(row.assignee_lastname)}`
  if (row.escalation_responsible) {
    return `${full} <span class="color-red">Take over: ${escapeHtml(row.escalation_responsible)}<span>`
  }
  return full
}

function assigneeHtml(row: TicketRow): string {
  if (row.assignee && Number(row.assignee) > 0) {
    return `<a href="javascript:void(0)" data-id="${Number(row.assignee)}" class="quick-sidebar-user">`
      + `<i class="fas fa-search" aria-hidden="true"></i> ${assigneeName(row)}</a>`
  }
  return '<label class="label label-danger font-10">Unassigned</label>'
}

/** Numele statusului: escaladarea si planificarea il inlocuiesc. */
export function statusName(escalationType: number, planned: number, name: string): string {
  if (escalationType) {
    switch (escalationType) {
      case 1:
        return 'Internal escalated'
      case 2:
        return 'Customer escalated'
      case 3:
        return 'External escalated'
      case 4:
        return 'Startechteam escalated'
      default:
        return 'Escalated'
    }
  }

  if (planned) return 'Planned'

  return name
}

/** Culoarea celulei de status: escaladarea bate planificarea, care bate statusul. */
export function statusColor(escalationColor: string | null, planningColor: string | null, color: string | null): string | null {
  let out = color
  if (planningColor) out = planningColor
  if (escalationColor) out = escalationColor
  return out
}

/** ShowSlaStatus: cele trei dungi colorate. */
export function slaStatusHtml(timers: TicketSlaStatus): string {
  return '<div style="display: inline-block;">'
    + `<div style="display:inline-block;margin-right:2px;width: 15px;height:28px;background-color: ${escapeHtml(timers.tookOverColor)}"></div>`
    + `<div style="display:inline-block;margin-right:2px;width: 15px;height:28px;background-color: ${escapeHtml(timers.identifyColor)}"></div>`
    + `<div style="display:inline-block;width: 15px;height:28px;background-color: ${escapeHtml(timers.interventionColor)}"></div>`
    + '</div>'
}

/** TicketActions: meniul din coloana Actions, cu butoanele pe care le da serverul. */
export function actionsHtml(id: number, actions: TicketActionFlags): string {
  const buttons: string[] = []

  if (actions.take_over === 1) {
    buttons.push(`<li><a href="#" class="quickTakeOver" data-id="${Number(id)}">`
      + '<i class="fa fa-user-plus" aria-hidden="true"></i> Take over</a></li>')
  }
  if (actions.comment === 1) {
    buttons.push(`<li><a href="#" class="showCommentModal" data-id="${Number(id)}"><i class="fas fa-comment" aria-hidden="true"></i> Comment</a></li>`)
  }
  if (actions.delete === 1) {
    buttons.push(`<li><a href="#" class="deleteTicket color-red" data-id="${Number(id)}">`
      + '<i class="fas fa-trash" aria-hidden="true"></i> Move to trash</a></li>')
  }

  if (buttons.length === 0) return ''

  return '<div class="dropdown">'
    + '<button class="btn btn-default btn-xs dropdown-toggle" data-toggle="dropdown">'
    + '<i class="dropdown-icon fas fa-angle-down"></i>'
    + '</button>'
    + '<ul class="dropdown-menu  pull-right">'
    + buttons.join('')
    + '</ul></div>'
}

/** Celula unei date: "acum 3 ore", cu data lunga in tooltip. */
function whenHtml(value: string | null | undefined): string {
  const when = stamp(value)
  if (!when) return ''
  return `<span data-toggle="tooltip" data-placement="top" title="${escapeHtml(longDateTime(when))}">${fromNow(when)}</span>`
}

/** Celula sursei tichetului (eticheta colorata cu iconita ei). */
function sourceHtml(row: TicketRow): string {
  if (!row.ticket_source) return ''
  return `<label data-toggle="tooltip" data-placement="top" title="${escapeHtml(row.ticketSourceName)}" class="label label-source" style="background-color:${escapeHtml(row.ticketSourceColor)}"><i class="${escapeHtml(row.ticketSourceIcon)}" aria-hidden="true"></i></label>`
}

function groupHtml(row: TicketRow): string {
  if (!row.group_name) return ''
  return `<i class="fas fa-circle" style="color:${escapeHtml(row.group_color)}" aria-hidden="true"></i> <a href="#" class="show-group-details">${escapeHtml(row.group_name)}</a>`
}

/**
 * Coloana SLA a listei active: termenul, si sub el timpul scurs, colorat.
 * Peste termen rosu, peste 75% portocaliu, altfel verde.
 */
function slaHtml(row: TicketRow): string {
  if (row.slaClock) return slaClockHtml(row.slaClock)

  const sla = Number(row.sla_time)
  if (!sla || sla <= 0) return ''

  const spent = Number(row.ticket_active_time)
  const head = `<i class="far fa-handshake" aria-hidden="true"></i> ${durationString(sla * 60)}`
  let color = 'green'
  if (spent > sla * 60) color = 'red'
  else if (spent >= (sla * 60 * 75) / 100) color = 'orange'

  return `${head}<div style="color:${color}"><i class="far fa-clock" aria-hidden="true"></i> ${hoursString(spent)}</div>`
}

/** Aceeasi coloana pe lista inchisa: doar rosu / verde, fara pragul de 75%. */
function closedSlaHtml(row: TicketRow): string {
  if (row.slaClock) return slaClockHtml(row.slaClock, true)

  const sla = Number(row.sla_time)
  if (!sla || sla <= 0) return ''

  const spent = Number(row.ticket_active_time)
  const head = `<i class="far fa-handshake" aria-hidden="true"></i> ${durationString(sla * 60)}`
  const color = sla * 60 < spent ? 'red' : 'green'

  return `${head}<div style="color:${color}"><i class="far fa-clock" aria-hidden="true"></i> ${hoursString(spent)}</div>`
}

/*
 * Titlurile de HTML se scriu PE LOC in fiecare coloana, nu printr-o constanta:
 * `tools/v4_identic.py` compara lista noua cu cea veche citind literalii din
 * cod, si nu poate urmari o constanta. Un titlu ascuns intr-o variabila ar
 * trece drept "alt titlu" si ar strica proba care apara regula „identic".
 */

/** TicketsModel - lista Easeedesk :: Ticket list. */
export function ticketColumns(): TicketColumn[] {
  return [
    {
      index: 0,
      title: '+',
      titleHtml: '<span class="hidden">+</span>',
      orderable: false,
      visible: true,
      width: 30,
      html: expandHtml,
    },
    {
      index: 1,
      title: 'ID',
      titleHtml: '<i class="fa fa-hashtag" aria-hidden="true"></i><span class="hidden">ID</span>',
      name: 'it_easeedesk_tickets.ID',
      orderable: true,
      visible: true,
      width: 60,
      html: (row) => `<strong><a href="${ticketHref(row.ID)}" target="_blank">${Number(row.ID)}</a></strong>`,
    },
    {
      index: 2,
      title: 'Type',
      name: 'ticket_type',
      orderable: true,
      visible: true,
      width: 64,
      html: (row) =>
        row.tType
          ? `<a href="${ticketHref(row.ID)}" target="_blank"><span class="badge ticket-type-badge" data-toggle="tooltip" data-placement="top" title="${escapeHtml(row.tType.type_name)}" style="background-color:${escapeHtml(row.tType.color_identifier)}">${escapeHtml(row.tType.short_name)}</span></a>`
          : '',
    },
    {
      index: 3,
      title: 'Subject',
      name: 'subject',
      orderable: true,
      visible: true,
      width: 150,
      html: subjectHtml,
      // chenarul rosu al tichetului XDR, si cu randul strans
      rowClass: (row) => (row.xdr ? 'ticket-xdr-row' : undefined),
    },
    {
      index: 4,
      title: 'Company',
      name: 'company_id',
      orderable: true,
      visible: true,
      width: 130,
      html: (row) =>
        `<a href="javascript:void(0)" class="show-company-details" style="color:${escapeHtml(row.company_color)}" data-id="${Number(row.company_id)}"><i class="fas fa-circle" aria-hidden="true"></i> ${escapeHtml(row.company_name)}</a>`,
    },
    {
      index: 5,
      title: 'Office',
      name: 'office_id',
      orderable: true,
      visible: true,
      width: 90,
      html: (row) =>
        row.office_name
          ? `<a href="javascript:void(0)" class="show-office-details"  style="color:${escapeHtml(row.office_color ? row.office_color : '#8A8A8A')}" data-id="${Number(row.office_id)}"><i class="fas fa-search" aria-hidden="true"></i> ${escapeHtml(row.office_name)}</a>`
          : '',
    },
    {
      index: 6,
      title: 'Affected User/Device',
      name: 'affected_user',
      orderable: true,
      visible: true,
      width: 110,
      html: affectedHtml,
    },
    {
      index: 7,
      title: 'Priority',
      name: 'ticket_priority',
      orderable: true,
      visible: true,
      width: 90,
      html: (row) =>
        row.tPriority
          ? `<span class="label ticket-priority-label" style="background-color:${escapeHtml(row.tPriority.priority_color)} ">${escapeHtml(row.tPriority.priority_name)}</span>`
          : '',
    },
    {
      index: 8,
      title: 'Category',
      name: 'ticket_category',
      orderable: true,
      visible: true,
      html: (row) => (row.tCategory ? `<span>${escapeHtml(row.tCategory.category_name)}</span>` : ''),
    },
    {
      index: 9,
      title: 'Subcategory',
      name: 'ticket_subcategory',
      orderable: true,
      visible: true,
      html: (row) => (row.tSubcategory ? `<span>${escapeHtml(row.tSubcategory.subcategory_name)}</span>` : ''),
    },
    {
      index: 10,
      title: 'SLA',
      name: 'sla_time',
      orderable: true,
      visible: true,
      html: slaHtml,
    },
    {
      index: 11,
      title: 'SLA Details',
      orderable: true,
      visible: true,
      /*
       * DEFECT MOSTENIT, REPARAT.
       *
       * Portalul citea aici `slaStatus.sla`, `.time` si `.color` - trei
       * campuri pe care `TicketTimersResponse` nu le-a avut niciodata (el are
       * tookOver*, identify*, intervention*). Coloana arata deci "0s" peste
       * "0s", cu `color : undefined` in stil, de cand exista.
       *
       * Acum citeste cronometrul AFLAT IN CURS - `currentSla`, `currentTime`,
       * `currentColor` - adica exact acela despre care vorbesc `timeMsg` si
       * `statusMsg`, si singurul pe care il alegea si portalul vechi cand
       * scria un termen si un timp la singular.
       */
      html: (row) => {
        if (!row.slaStatus) return ''
        const timers = row.slaStatus
        return `${durationString(timers.currentSla)}<div style="color : ${escapeHtml(timers.currentColor)}">${durationString(timers.currentTime)}</div>`
      },
    },
    {
      index: 12,
      title: 'Created by',
      name: 'creator',
      orderable: true,
      visible: true,
      html: (row) => {
        if (!row.ticket_creator) return ''
        let result = ''
        if (Number(row.vip) === 1) {
          result += '<span class="label label-danger label-bolded"><i class="fa fa-star"></i> VIP</span> '
        }
        const name = escapeHtml(row.ticket_creator)
        const type = Number(row.creator_type)
        if (type === 1) {
          result += `<a href="#" title="${name}" data-id="${Number(row.creator)}" class="quick-sidebar-user">`
            + `<i class="fas fa-search" aria-hidden="true"></i> ${trunc(name, 30)}</a>`
        } else if (type === 2) {
          result += `<a href="#" title="${name}" data-id="${Number(row.creator)}" class="quick-sidebar-owner">`
            + `<i class="fas fa-search" aria-hidden="true"></i> ${trunc(name, 30)}</a>`
        } else {
          result += `<span title="${name}">${trunc(name, 20)}</span>`
        }
        return result
      },
    },
    {
      index: 13,
      title: 'Source',
      name: 'ticket_source',
      orderable: true,
      visible: true,
      html: sourceHtml,
    },
    {
      index: 14,
      title: 'Assigned Group',
      name: 'assigned_group',
      orderable: true,
      visible: true,
      html: groupHtml,
    },
    {
      index: 15,
      title: 'Assignee',
      name: 'assignee',
      orderable: true,
      visible: true,
      html: assigneeHtml,
    },
    {
      index: 16,
      title: 'Created',
      name: 'created',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.created),
    },
    {
      index: 17,
      title: 'Updated',
      name: 'updated',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.updated),
    },
    {
      index: 18,
      title: 'Resolved',
      name: 'resolved',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.resolved),
    },
    {
      index: 19,
      title: 'SLA Status',
      orderable: true,
      visible: true,
      width: 58,
      html: (row) => (row.slaStatus ? slaStatusHtml(row.slaStatus) : ''),
    },
    {
      index: 20,
      title: 'Multiple select',
      titleHtml: '<input type="checkbox" class="all-tickets"/><span class="hidden">Multiple select</span>',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) => `<input type="checkbox" data-id="${Number(row.ID)}" class="select-ticket"${row.__checked ? ' checked' : ''}/>`,
    },
    {
      index: 21,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 40,
      html: (row) => actionsHtml(row.ID, row.actions ?? {}),
    },
    {
      index: 22,
      title: 'Status',
      name: 'ticket_status',
      orderable: true,
      visible: true,
      width: 75,
      html: (row) =>
        row.tStatus
          ? `<span class="label ticket-status-label">${escapeHtml(statusName(Number(row.escalation_type), Number(row.planned), String(row.tStatus.status_name ?? '')))}</span>`
          : '',
      /* createdCell: fundalul celulei de status, si randul VIP. */
      cellStyle: (row) => {
        if (!row.tStatus) return undefined
        const color = statusColor(row.escalation_color, row.planning_color, row.tStatus.status_color ?? null)
        return color ? { backgroundColor: `${color}`, color: '#FFFFFF' } : undefined
      },
      rowClass: (row) => (Number(row.vip) === 1 || Number(row.tPriority?.urgency_flag) === 1 ? 'vip' : undefined),
    },
  ]
}

/** ClosedTicketsModel - lista Easeedesk :: Closed Tickets list. */
export function closedTicketColumns(): TicketColumn[] {
  return [
    {
      index: 0,
      title: '+',
      titleHtml: '<span class="hidden">+</span>',
      orderable: false,
      visible: true,
      width: 30,
      html: expandHtml,
    },
    {
      index: 1,
      title: 'ID',
      titleHtml: '<i class="fa fa-hashtag" aria-hidden="true"></i><span class="hidden">ID</span>',
      name: 'it_easeedesk_closed_tickets.ID',
      orderable: true,
      visible: false,
      html: (row) => `<strong>${Number(row.ID)}</strong>`,
    },
    {
      index: 2,
      title: 'Type',
      name: 'ticket_type',
      orderable: true,
      visible: true,
      html: (row) =>
        row.tType
          ? `<span class="badge ticket-type-badge" data-toggle="tooltip" data-placement="top" title="${escapeHtml(row.tType.type_name)}" style="background-color:${escapeHtml(row.tType.color_identifier)}">${escapeHtml(row.tType.short_name)}</span>`
          : '',
    },
    {
      index: 3,
      title: 'Subject',
      name: 'subject',
      orderable: true,
      visible: true,
      /* Aceeasi celula ca pe lista activa, minus insigna de chat, care acolo
         n-are numar in titlu. Vezi closed_tickets.js. */
      html: (row) => {
        let badge = appLabelHtml(row.app_label)
        if (Number(row.chat_messages) > 0) {
          badge += `<i class="fa fa-comment color-red font-14" data-toggle="tooltip" data-placement="top" title="${Number(row.chat_messages)} new chat messages" aria-hidden="true"></i> `
        }
        if (Number(row.conversation_messages) > 0) {
          badge += `<i class="fa fa-envelope font-14 color-warning" data-toggle="tooltip" data-placement="top" title="${Number(row.conversation_messages)} new conversation messages" aria-hidden="true"></i> `
        }
        if (Number(row.comments_messages) > 0) {
          badge += `<i class="far fa-comments font-14 color-red" data-toggle="tooltip" data-placement="top" title="${Number(row.comments_messages)} new comments" aria-hidden="true"></i> `
        }
        const subject = escapeHtml(row.subject)
        if (Number(row.attachments) > 0) {
          return `${badge}<i class="fa fa-paperclip" aria-hidden="true" data-toggle="tooltip" data-placement="top" title="${Number(row.attachments)} Attachments"></i> ${subject}`
        }
        return `${badge}<a href="${ticketHref(row.ID)}" target="_blank" style="color:#57636f">${subject}</a>`
      },
    },
    {
      index: 4,
      title: 'Company',
      name: 'company_id',
      orderable: true,
      visible: false,
      html: (row) =>
        `<a href="#" class="show-company-details" data-id="${Number(row.company_id)}"><i class="fas fa-search" aria-hidden="true"></i> ${escapeHtml(row.company_name)}</a>`,
    },
    {
      index: 5,
      title: 'Office',
      name: 'office_id',
      orderable: true,
      visible: false,
      html: (row) =>
        row.office_name
          ? `<a href="javascript:void(0)" class="show-office-details" data-id="${Number(row.office_id)}"><i class="fas fa-search" aria-hidden="true"></i> ${escapeHtml(row.office_name)}</a>`
          : '',
    },
    {
      index: 6,
      title: 'Affected User/Device',
      name: 'affected_user',
      orderable: true,
      visible: true,
      html: affectedHtml,
    },
    {
      index: 7,
      title: 'Priority',
      name: 'ticket_priority',
      orderable: true,
      visible: true,
      /* Lista inchisa n-are clasa `ticket-priority-label`, ci `font-10`. */
      html: (row) =>
        row.tPriority
          ? `<span class="label font-10" style="background-color:${escapeHtml(row.tPriority.priority_color)} ">${escapeHtml(row.tPriority.priority_name)}</span>`
          : '',
    },
    {
      index: 8,
      title: 'Category',
      name: 'ticket_category',
      orderable: true,
      visible: true,
      html: (row) => (row.tCategory ? `<span>${escapeHtml(row.tCategory.category_name)}</span>` : ''),
    },
    {
      index: 9,
      title: 'Subcategory',
      name: 'ticket_subcategory',
      orderable: true,
      visible: true,
      html: (row) => (row.tSubcategory ? `<span>${escapeHtml(row.tSubcategory.subcategory_name)}</span>` : ''),
    },
    {
      index: 10,
      title: 'SLA',
      name: 'sla_time',
      orderable: true,
      visible: false,
      html: closedSlaHtml,
    },
    {
      index: 11,
      title: 'Created by',
      name: 'creator',
      orderable: true,
      visible: true,
      /* Fara insigna VIP - lista inchisa n-o avea. */
      html: (row) => {
        if (!row.ticket_creator) return ''
        const name = escapeHtml(row.ticket_creator)
        const type = Number(row.creator_type)
        if (type === 1) {
          return `<a href="javascript:void(0)" title="${name}" data-id="${Number(row.creator)}" class="quick-sidebar-user">`
            + `<i class="fas fa-search" aria-hidden="true"></i> ${trunc(name, 30)}</a>`
        }
        if (type === 2) {
          return `<a href="javascript:void(0)" title="${name}" data-id="${Number(row.creator)}" class="quick-sidebar-owner">`
            + `<i class="fas fa-search" aria-hidden="true"></i> ${trunc(name, 30)}</a>`
        }
        return `<span title="${name}">${trunc(name, 20)}</span>`
      },
    },
    {
      index: 12,
      title: 'Source',
      name: 'ticket_source',
      orderable: true,
      visible: true,
      html: sourceHtml,
    },
    {
      index: 13,
      title: 'Assigned Group',
      name: 'assigned_group',
      orderable: true,
      visible: true,
      html: groupHtml,
    },
    {
      index: 14,
      title: 'Assignee',
      name: 'assignee',
      orderable: true,
      visible: true,
      html: assigneeHtml,
    },
    {
      index: 15,
      title: 'Created',
      name: 'created',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.created),
    },
    {
      index: 16,
      title: 'Updated',
      name: 'updated',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.updated),
    },
    {
      index: 17,
      title: 'Resolved',
      name: 'resolved',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.resolved),
    },
    {
      index: 18,
      title: 'Closed',
      name: 'closed',
      orderable: true,
      visible: true,
      html: (row) => whenHtml(row.closed),
      /* createdCell: celula "Closed" are fundalul ei, mereu. */
      cellStyle: () => ({ backgroundColor: '#eee' }),
    },
    {
      index: 19,
      title: 'Status',
      name: 'ticket_status',
      orderable: true,
      visible: true,
      html: (row) =>
        row.tStatus
          ? `<label class="label ticket-status-label">${escapeHtml(statusName(Number(row.escalation_type), Number(row.planned), String(row.tStatus.status_name ?? '')))}</label>`
          : '',
      cellStyle: (row) => {
        if (!row.tStatus) return undefined
        const color = statusColor(row.escalation_color, row.planning_color, row.tStatus.status_color ?? null)
        return color ? { backgroundColor: `${color}`, color: '#FFF' } : undefined
      },
    },
  ]
}
